import React from 'react';
import classNames from 'classnames';
import {withI18n, WithI18nProps} from 'packages/@shopify/react-i18n';
import {Shop, Filter, SortOption, PaginationState} from 'interfaces';
import {Grid, GridItem} from 'components/MarketingAssets';
import {Flex} from 'components/UIKit';
import {ShopFilters, filtersHaveSelection} from 'components/ShopFilters';
import {ShopDisplay} from 'components/ShopDisplay';
import ShopDisplayToggle from 'components/ShopDisplayToggle';
import {getShops, ShopsHistoryState} from 'utilities/shopsApi';
import {FeaturedListings, HomeHeader} from './components';
import * as locales from './locales';

export interface FeaturedSection {
  shops: Shop[];
  key: string;
  linkUrl: string;
  count: number;
}

export interface HomeProps {
  displayAsTile: boolean;
  filters: Filter[];
  sortOptions: SortOption[];
  featuredListings: FeaturedSection[];
  shopsPath: string;
  sortBy: string;
}

export interface HomeState {
  shops: Shop[];
  sortBy: string;
  showShops: boolean;
  pagination?: PaginationState;
  filterState: Filter[];
  nextPageURL?: string;
  shopsLoading: boolean;
  displayAsTile: boolean;
  shopsCountString?: string;
}

const style = {
  section: 'section',
  offWhite: 'background-off-white',
  homeHeader: 'section--home-header',
  seeMoreWrap: 'grid__item--full text-center',
  sectionTight: 'section--tight',
  marketingButton: 'marketing-button',
  smartSearchZeroResults: 'smart-search__zero-results',
};

const sectionClassName = classNames(
  style.section,
  style.offWhite,
  style.homeHeader,
  style.sectionTight,
);

const UPDATE_ON_POPOVER_CLOSE = true;

// @TODO: These could perhaps happen in controller
const DEFAULT_SORT = 'average_monthly_revenue_high_to_low';
const HIDE_FILTERS = ['featured_category_ids'];

function parseFilters(filters: Filter[]): Filter[] {
  return filters.filter((filter) => {
    if (HIDE_FILTERS.includes(filter.type)) {
      return false;
    }

    if (!filter.options.length) {
      return false;
    }

    return true;
  });
}

function NOOP() {}

class HomeComponent extends React.Component<HomeProps & WithI18nProps, HomeState> {
  static displayName = 'Home';
  pendingUpdate: boolean;
  activePopover: string;

  constructor(props) {
    super(props);

    const filterState = parseFilters(props.filters);

    this.pendingUpdate = false;
    this.activePopover = '';

    this.state = {
      shops: [],
      sortBy: props.sortBy || DEFAULT_SORT,
      showShops: false,
      filterState,
      shopsLoading: false,
      displayAsTile: props.displayAsTile,
    };
  }

  /**
   * Flush any pending API updates
   */
  clearPendingUpdates = () => {
    if (this.pendingUpdate) {
      this.getShopsFromApi();
      this.pendingUpdate = false;
    }
  };

  componentDidMount() {
    window.addEventListener('popstate', this.handlePopstate);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopstate);
  }

  /**
   * Handle popstate event,
   */
  handlePopstate = (event) => {
    // If popstate event has `state`, for now we can assume it is our ShopsHistoryState, so we serialize back to state
    if (event && event.state) {
      const {filters, sortBy, shops, shopsCountString, pagination} = (event.state as ShopsHistoryState);

      this.setState({
        shops: shops || [],
        sortBy: sortBy || DEFAULT_SORT,
        showShops: filtersHaveSelection(filters),
        filterState: parseFilters(filters),
        pagination,
        shopsCountString,
      }, shops.length ? this.getShopsFromApi : NOOP);
      return;
    }
    // If history state doesn't exist, we should reset to identity (state at page load)
    const {filters, sortBy} = this.props;

    this.setState((prevState) => {
      return {
        shops: [],
        sortBy: sortBy || DEFAULT_SORT,
        showShops: false,
        filterState: parseFilters(filters),
        shopsCountString: '',
        displayAsTile: prevState.displayAsTile,
      };
    });
  };

  /**
   * Retrieve a set of listings from the API based on the state of the component.
   * Should buffer the update if there is an active popover and `UPDATE_ON_POPOVER_CLOSE` is set.
   * If the filters are cleared, set the shops state to empty and early return.
   * Results should be serialized back to component state.
   */
  getShopsFromApi = async () => {
    const {shopsPath: path} = this.props;
    const {filterState: filters, sortBy} = this.state;

    if (UPDATE_ON_POPOVER_CLOSE && this.activePopover) {
      this.pendingUpdate = true;
      return;
    }

    if (!filtersHaveSelection(filters)) {
      this.setState({shops: [], showShops: false});
      history.pushState(false, document.title, location.pathname);
      return;
    }

    this.setState({shopsLoading: true});

    const shopsResponse = await getShops({path, sortBy, filters, queryToUrl: true});

    if (shopsResponse === null) {
      // @TODO: Error screen? Retry?
      this.setState({
        showShops: false,
        shopsLoading: false,
      });
      return;
    }

    const {
      shops,
      filters: filterState,
      pagination,
      nextPageURL,
      shopsCountString,
    } = shopsResponse;

    this.setState({
      shops,
      showShops: filtersHaveSelection(filterState),
      pagination,
      nextPageURL,
      filterState: parseFilters(filterState),
      shopsLoading: false,
      shopsCountString,
    });
  };

  /**
   * Handle change events from `ShopDisplayToggle`
   */
  handleDisplayChange = () => {
    this.setState((prevState) => ({
      displayAsTile: !prevState.displayAsTile,
    }));
  };

  /**
   * Handle filter updates from the `ShopFilters` component
   */
  handleFilterUpdate = (filterState) => {
    this.setState({
      filterState: parseFilters(filterState),
    }, this.getShopsFromApi);
  };

  /**
   * Handle popover state change events from `ShopFilters`
   * @param filterType The open popover. If closed, will be empty string.
   */
  handlePopoverState = (filterType) => {
    this.activePopover = filterType;
    if (UPDATE_ON_POPOVER_CLOSE) {
      this.clearPendingUpdates();
    }
  };

  /**
   * Handle change events from `FilterSort`.
   * Sets the `sortBy` state locally, and triggers an API call
   */
  handleSortChange = (sortBy) => this.setState({sortBy}, this.getShopsFromApi);

  renderShops = () => {
    const {i18n} = this.props;
    const {
      shops,
      pagination,
      nextPageURL,
      displayAsTile,
      shopsCountString = '',
    } = this.state;

    const showSeeMore = Boolean(shops.length && (pagination && (pagination.totalCount > shops.length)));

    return (
      <GridItem>
        <ShopDisplay
          tile={displayAsTile}
          shops={shops}
          countString={shopsCountString}
        />
        {showSeeMore && (
          <GridItem className={style.seeMoreWrap}>
            <a href={nextPageURL} className={style.marketingButton}>
              {i18n.translate('seeMoreListings')}
            </a>
          </GridItem>
        )}
      </GridItem>
    );
  };

  renderFeaturedListings = () => {
    const {featuredListings} = this.props;

    return featuredListings.map((feature, index) => (
      <GridItem key={feature.key}>
        <FeaturedListings
          first={index === 0}
          feature={feature}
        />
      </GridItem>
    ));
  };

  render() {
    const {sortOptions} = this.props;
    const {
      sortBy,
      shops = [],
      showShops,
      filterState: filters = [],
      shopsLoading,
      displayAsTile,
    } = this.state;

    return (
      <React.Fragment>
        <section className={sectionClassName}>
          <Grid>
            <GridItem>
              <HomeHeader />
            </GridItem>
            <GridItem>
              <Flex>
                <ShopFilters
                  sortBy={sortBy}
                  filters={filters}
                  disabled={shopsLoading}
                  showSort={Boolean(shops.length)}
                  sortOptions={sortOptions}
                  onSortChange={this.handleSortChange}
                  onFilterUpdate={this.handleFilterUpdate}
                  onPopoverChange={this.handlePopoverState}
                />
                <ShopDisplayToggle
                  tile={displayAsTile}
                  hidden={!showShops}
                  onToggle={this.handleDisplayChange}
                />
              </Flex>
            </GridItem>
          </Grid>
        </section>
        <section>
          <Grid equalHeight>
            {showShops
              ? this.renderShops()
              : this.renderFeaturedListings()
            }
          </Grid>
        </section>
      </React.Fragment>
    );
  }
}

export const Home = withI18n({
  id: 'Home',
  fallback: locales.en,
})(HomeComponent);

export default Home;
